import clsx from "clsx";
import { useMemo } from "react";
import { ScanItem } from "./ScanItem";
import Accordion from "@/components/Accordion/Accordion";
import { AppText } from "@/components/Text/AppText";
import { IScanResultItem, ScanKeysLabels } from "@/types/atsScan";
import { getScoreBadgeClasses } from "@/utils/scoreColors";

interface ScanSectionProps {
    sectionKey: string;
    sectionData: Record<string, IScanResultItem>;
    defaultOpen?: boolean;
}

export const ScanSection = ({
    sectionKey,
    sectionData,
    defaultOpen = false,
}: ScanSectionProps) => {
    const sectionItems = Object.entries(sectionData);
    const sortedSectionItems = useMemo(
        () =>
            sectionItems.sort((a, b) => {
                if (a[1].priority === "high") return 1;
                if (a[1].priority === "medium") return 0;
                return -1;
            }),
        [sectionItems],
    );

    return (
        <div className="mb-2">
            <Accordion
                title={
                    <div className="mr-2 flex w-full items-center justify-between gap-2">
                        <AppText
                            variant="contextheading"
                            className="!mb-0"
                        >
                            {
                                ScanKeysLabels[
                                    sectionKey as keyof typeof ScanKeysLabels
                                ]
                            }
                        </AppText>
                        <ScoreBadge sectionItems={sectionItems} />
                    </div>
                }
                variant="white"
                defaultOpen={defaultOpen}
                width="full"
            >
                <div className="space-y-6">
                    {sortedSectionItems.map(([itemKey, item]) => (
                        <ScanItem
                            key={itemKey}
                            itemKey={itemKey}
                            item={item}
                        />
                    ))}
                </div>
            </Accordion>
        </div>
    );
};

const ScoreBadge = ({
    sectionItems,
}: {
    sectionItems: [string, IScanResultItem][];
}) => {
    const sectionScore = useMemo(() => {
        const aggregatedScore = sectionItems.reduce(
            (acc, [, item]) => acc + item.score,
            0,
        );
        const maxScore = sectionItems.reduce(
            (acc, [, item]) => acc + item.max_score,
            0,
        );
        const score = (aggregatedScore / maxScore) * 100;
        return score;
    }, [sectionItems]);

    const scoreColor = useMemo(() => {
        return getScoreBadgeClasses(sectionScore);
    }, [sectionScore]);

    return (
        <div
            className={clsx(
                "w-[55px] rounded-lg px-2 py-0.5 text-center",
                scoreColor,
            )}
        >
            <AppText variant="labelsbuttons">
                {sectionScore.toFixed(0)}%
            </AppText>
        </div>
    );
};
